/**
 * First-tap focus for editable text fields on touch.
 *
 * iOS Safari often spends the first tap blurring a previous control
 * (native `<input type="date">`, a focused chip button, a read-only
 * time field) instead of focusing the textarea the operator actually
 * hit. Calling `focus()` on the target field ourselves makes the
 * keyboard appear on that first tap — SpaceManager, Spatialist, and
 * SpaceStation all share this helper via the root layout listener.
 *
 * Focus happens on `pointerup` of a *tap*, never on `pointerdown`: a
 * finger that lands on a field to scroll the list must not raise the
 * keyboard. The browser cancels the pointer when a scroll starts, and
 * `isTouchTap` rejects a drag that never became one.
 */

/** A touch that travels farther than this is a drag, not a tap. */
export const TOUCH_TAP_SLOP_PX = 10;
/** A touch held longer than this is a press, not a tap. */
export const TOUCH_TAP_MAX_MS = 700;

export type TouchTapPoint = {
  pointerId: number | undefined;
  x: number;
  y: number;
  time: number;
};

export function isTouchTap(down: TouchTapPoint, up: TouchTapPoint): boolean {
  if (down.pointerId !== up.pointerId) return false;
  if (up.time - down.time > TOUCH_TAP_MAX_MS) return false;
  return (
    Math.hypot(up.x - down.x, up.y - down.y) <= TOUCH_TAP_SLOP_PX
  );
}

const SKIP_INPUT_TYPES = new Set([
  "button",
  "submit",
  "reset",
  "checkbox",
  "radio",
  "file",
  "hidden",
  "image",
  "range",
  "color",
  "date",
  "time",
  "datetime-local",
  "month",
  "week",
]);

export function isTouchEditableTextField(
  el: EventTarget | null,
): el is HTMLInputElement | HTMLTextAreaElement | HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.closest("[data-touch-focus-skip]")) return false;
  if (el instanceof HTMLTextAreaElement) {
    return !el.disabled && !el.readOnly;
  }
  if (el instanceof HTMLInputElement) {
    if (el.disabled || el.readOnly) return false;
    const type = (el.type || "text").toLowerCase();
    return !SKIP_INPUT_TYPES.has(type);
  }
  return el.isContentEditable;
}

function fieldFromLabel(label: HTMLLabelElement): HTMLElement | null {
  const control = label.control;
  return control instanceof HTMLElement ? control : null;
}

export function resolveTouchEditableTextField(
  target: EventTarget | null,
): HTMLElement | null {
  if (!(target instanceof Element)) return null;

  if (target instanceof HTMLLabelElement) {
    const fromLabel = fieldFromLabel(target);
    return isTouchEditableTextField(fromLabel) ? fromLabel : null;
  }

  const wrappingLabel = target.closest("label");
  if (wrappingLabel instanceof HTMLLabelElement) {
    const fromLabel = fieldFromLabel(wrappingLabel);
    if (isTouchEditableTextField(fromLabel)) return fromLabel;
  }

  const nested = target.closest(
    "textarea, input, [contenteditable='true'], [contenteditable='']",
  );
  return isTouchEditableTextField(nested) ? nested : null;
}

export function focusTouchEditableTextField(target: EventTarget | null): boolean {
  const field = resolveTouchEditableTextField(target);
  if (!field) return false;
  if (document.activeElement === field) return false;
  field.focus({ preventScroll: true });
  if (
    (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) &&
    typeof field.setSelectionRange === "function"
  ) {
    try {
      const end = field.value.length;
      field.setSelectionRange(end, end);
    } catch {
      /* some input types reject setSelectionRange */
    }
  }
  return document.activeElement === field;
}
